import React from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Link,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddLinkIcon from '@mui/icons-material/AddLink';
import moment from 'moment';
import store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import CreditAppSelectorDialog from '../../components/CreditAppSelectorDialog';

const STATUS_COLORS = {
  pending: '#ff9800',
  reviewed: '#2196f3',
  sent: '#9c27b0',
  approved: '#4caf50',
  declined: '#f44336',
};

export default function CreditAppsTab({ cust_id }) {
  const [apps, setApps] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectorOpen, setSelectorOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setApps(await store.getCreditAppsForCustomer(cust_id));
    } catch (e) {
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [cust_id]);

  React.useEffect(() => { load(); }, [load]);

  const handleLink = async (app) => {
    try {
      await store.linkCreditAppToCustomer(app.id, cust_id);
      StateManager.setAlertAndOpen('Credit app linked to customer', 'success');
      load();
    } catch (e) {
      StateManager.setAlertAndOpen('Could not link credit app', 'error');
    }
  };

  const handleUnlink = async (app) => {
    try {
      await store.unlinkCreditAppFromCustomer(app.id);
      StateManager.setAlertAndOpen('Credit app unlinked', 'success');
      load();
    } catch (e) {
      StateManager.setAlertAndOpen('Could not unlink credit app', 'error');
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Credit Applications</Typography>
        <Button variant="contained" size="small" startIcon={<AddLinkIcon />} onClick={() => setSelectorOpen(true)}>
          Link Credit App
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      )}

      {!loading && apps.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No credit applications linked to this customer yet.
        </Typography>
      )}

      {!loading && apps.map(app => {
        const status = app.status || 'pending';
        return (
          <Card key={app.id} variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {app.first_name} {app.last_name}
                  </Typography>
                  <Chip
                    size="small"
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                    sx={{ backgroundColor: STATUS_COLORS[status] || '#9e9e9e', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {app.car_title ? `${app.car_title}${app.stock ? ` (#${app.stock})` : ''} · ` : ''}
                  Submitted {app.time ? moment(app.time, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY') : 'N/A'}
                </Typography>
                {app.location && (
                  <Link href={app.location} target="_blank" rel="noopener" variant="caption">View PDF</Link>
                )}
              </Box>
              <Tooltip title="Open credit app">
                <IconButton size="small" onClick={() => StateManager.openCreditAppModal(app.id)}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unlink from this customer">
                <IconButton size="small" color="error" onClick={() => handleUnlink(app)}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
        );
      })}

      <CreditAppSelectorDialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={handleLink}
        customerId={cust_id}
      />
    </Box>
  );
}
